import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";

const DCRLeaveTypePage = ({ hideCurrentMonth = false }) => {
  const navigate = useNavigate();

  /* ===================== MASTER DATA ===================== */
  const [divisions, setDivisions] = useState([]);
  const [states, setStates] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);

  /* ===================== SELECTED FILTERS ===================== */
  const [selectedDivisions, setSelectedDivisions] = useState([]); // NAME
  const [selectedStates, setSelectedStates] = useState([]); // ID
  const [selectedDesignations, setSelectedDesignations] = useState([]); // NAME
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);

  const [selectedRetailerCategories, setSelectedRetailerCategories] = useState(
    []
  ); // chemist, doctor
  const [selectedVisitCategories, setSelectedVisitCategories] = useState([]); // notVisit, notPob
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));

  /* ===================== COUNTS ===================== */
  const [divisionCounts, setDivisionCounts] = useState({});
  const [stateCounts, setStateCounts] = useState({});
  const [designationCounts, setDesignationCounts] = useState({});

  /* ===================== MONTH ===================== */
  const now = dayjs();
  const months = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 12; i++) {
      const m = now.subtract(i, "month");
      arr.push({
        label: m.format("MMMM YYYY"),
        value: m.format("YYYY-MM"),
        isCurrent: i === 0,
      });
    }
    return arr
      .slice(0, arr.length - 3)
      .filter((m) => !(hideCurrentMonth && m.isCurrent));
  }, [hideCurrentMonth, now]);

  /* ===================== STATIC FILTERS ===================== */
  const retailerCategories = [
     { id: "chemist", name: "Chemist / Retailer" },
    { id: "doctor", name: "Doctor" },
  ];

  const visitCategories = [
    { id: "notVisit", name: "Not Visit" },
    { id: "notPob", name: "Not POB" },
  ];

  /* ===================== RESET CHILD FILTERS ===================== */
  useEffect(() => {
    if (!selectedDivisions.length) setSelectedStates([]);
  }, [selectedDivisions]);
  useEffect(() => {
    if (!selectedStates.length) setSelectedDesignations([]);
  }, [selectedStates]);
  useEffect(() => {
    if (!selectedDesignations.length) setSelectedEmployees([]);
  }, [selectedDesignations]);

  /* ===================== LOAD MASTER DATA ===================== */
  useEffect(() => {
    const loadMaster = async () => {
      const [divRes, stateRes, desRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/designations/`, {
          headers: getAuthHeaders(),
        }),
      ]);
      setDivisions(divRes.data);
      setStates(stateRes.data);
      setDesignations(desRes.data);
    };
    loadMaster();
  }, []);

  /* ===================== FETCH EMPLOYEES (MAIN FILTER LOGIC) ===================== */
  useEffect(() => {
    if (
      !selectedDivisions.length ||
      !selectedStates.length ||
      !selectedDesignations.length
    ) {
      setEmployees([]);
      return;
    }

    const params = {
      division: selectedDivisions, // NAME
      designation: selectedDesignations, // NAME
      state_id: selectedStates, // ID
      retailer_type: selectedRetailerCategories.length
        ? selectedRetailerCategories
        : undefined,
      not_visit: selectedVisitCategories.includes("notVisit"),
      not_pob: selectedVisitCategories.includes("notPob"),
    };

    axios
      .get(`${BASE_URL}/api/employees-filter`, {
        headers: getAuthHeaders(),
        params,
        paramsSerializer: { indexes: null }, // 🔥 remove []
      })
      .then((res) => setEmployees(res.data))
      .catch(() => setEmployees([]));
  }, [
    selectedDivisions,
    selectedStates,
    selectedDesignations,
    selectedRetailerCategories,
    selectedVisitCategories,
  ]);
 
  /* ===================== UPDATE COUNTS ===================== */
  useEffect(() => {
  axios
    .get(`${BASE_URL}/api/employees`, { headers: getAuthHeaders() })
    .then((res) => setAllEmployees(res.data))
    .catch(() => setAllEmployees([]));
}, []);

useEffect(() => {
  const div = {}, st = {}, des = {};

  allEmployees.forEach((e) => {
    div[e.division] = (div[e.division] || 0) + 1;
    st[e.state_id] = (st[e.state_id] || 0) + 1;
    des[e.designation] = (des[e.designation] || 0) + 1;
  });

  setDivisionCounts(div);
  setStateCounts(st);
  setDesignationCounts(des);
}, [allEmployees]);



  /* ===================== HELPERS ===================== */
  const toggle = (value, list, setList) => {
    setList((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const selectAll = (items, list, setList, key) => {
    if (list.length === items.length) {
      setList([]);
      return;
    }
    if (key === "division") setList(items.map((i) => i.name));
    else if (key === "designation") setList(items.map((i) => i.name));
    else if (key === "state") setList(items.map((i) => i.id));
    else if (key === "employee") setList(items.map((i) => i.id));
    else if (key === "retailer" || key === "visit")
      setList(items.map((i) => i.id));
  };

  /* ===================== SUBMIT ===================== */
 const handleSubmit = async () => {
  if (!selectedMonth || !selectedEmployees.length) {
    alert("Please select DCR Month and Employees");
    return;
  }

  if (!selectedRetailerCategories.length) {
    alert("Please select Chemist or Doctor");
    return;
  }

  const monthLabel = dayjs(selectedMonth).format("MMM YYYY");

  const params = {
    monthYear: monthLabel,
  };
  if (selectedRetailerCategories.length === 1) {
    params.retailer_type = selectedRetailerCategories[0];
  }
  if (selectedVisitCategories.includes("notVisit")) {
    params.not_visit = true;
  }

  if (selectedVisitCategories.includes("notPob")) {
    params.not_pob = true;
  }

  await axios.post(
    `${BASE_URL}/api/employees-activate-retailers`,
    { employee_ids: selectedEmployees },
    {
      headers: getAuthHeaders(),
      params,
    }
  );

  alert("Updated successfully");

  setSelectedEmployees([]);
  setSelectedRetailerCategories([]);
  setSelectedVisitCategories([]);
  setSelectedMonth("");
};


  /* ===================== UI ===================== */
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="bg-white border rounded-xl shadow p-4">
        {/* Header */}
        <div className="flex justify-center border-b pb-3 mb-4">
          <h1 className="text-xl md:text-2xl font-bold">
            <span className="text-blue-700">DCR</span>{" "}
            <span className="text-blue-500">Visitors</span>
          </h1>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <FilterBox
            title="State"
            items={states}
            selected={selectedStates}
            onSelectAll={() =>
              selectAll(states, selectedStates, setSelectedStates, "state")
            }
            onToggle={(v) => toggle(v, selectedStates, setSelectedStates)}
            valueKey="id"
            labelKey="name"
            countMap={stateCounts}
            cols={2}
          />

          <FilterBox
            title="Division"
            items={divisions}
            selected={selectedDivisions}
            onSelectAll={() =>
              selectAll(
                divisions,
                selectedDivisions,
                setSelectedDivisions,
                "division"
              )
            }
            onToggle={(v) => toggle(v, selectedDivisions, setSelectedDivisions)}
            valueKey="name"
            labelKey="name"
            countMap={divisionCounts}
            cols={1}
          />

          <FilterBox
            title="Designation"
            items={designations}
            selected={selectedDesignations}
            onSelectAll={() =>
              selectAll(
                designations,
                selectedDesignations,
                setSelectedDesignations,
                "designation"
              )
            }
            onToggle={(v) =>
              toggle(v, selectedDesignations, setSelectedDesignations)
            }
            valueKey="name"
            labelKey="name"
            countMap={designationCounts}
            cols={3}
          />
        </div>

        {/* Retailer / Visit */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <SimpleBox
            title="Retailer / Doctor"
            items={retailerCategories}
            selected={selectedRetailerCategories}
            onSelectAll={() =>
              selectAll(
                retailerCategories,
                selectedRetailerCategories,
                setSelectedRetailerCategories,
                "retailer"
              )
            }
            onToggle={(v) =>
              toggle(
                v,
                selectedRetailerCategories,
                setSelectedRetailerCategories
              )
            }
          />

          <SimpleBox
            title="Visit / POB"
            items={visitCategories}
            selected={selectedVisitCategories}
            onSelectAll={() =>
              selectAll(
                visitCategories,
                selectedVisitCategories,
                setSelectedVisitCategories,
                "visit"
              )
            }
            onToggle={(v) =>
              toggle(v, selectedVisitCategories, setSelectedVisitCategories)
            }
          />
        </div>

        {/* Employees */}
        <div className="border rounded-lg p-3 mb-6">
          <label className="flex justify-between font-semibold mb-2">
            Employees ({employees.length})
            <input
              type="checkbox"
              checked={
                employees.length > 0 &&
                selectedEmployees.length === employees.length
              }
              onChange={() =>
                selectAll(
                  employees,
                  selectedEmployees,
                  setSelectedEmployees,
                  "employee"
                )
              }
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto text-sm">
            {employees.map((emp) => (
              <label key={emp.id} className="flex gap-2">
                <input
                  type="checkbox"
                  checked={selectedEmployees.includes(emp.id)}
                  onChange={() =>
                    toggle(emp.id, selectedEmployees, setSelectedEmployees)
                  }
                />
                {`${emp.first_name} ${emp.last_name} (${
                  emp.city_name || "N/A"
                } | ${emp.designation || "N/A"} | ${emp.division || "N/A"})`}
              </label>
            ))}
          </div>
        </div>

        {/* Month */}
        <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
          <label className="font-semibold">DCR Month</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="border rounded px-3 py-1 w-full md:w-64"
          >
            <option value="">Select Month</option>
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex justify-center gap-4">
          <button
            onClick={handleSubmit}
            className="bg-green-600 text-white px-6 py-2 rounded"
          >
            Submit
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-red-600 text-white px-6 py-2 rounded"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DCRLeaveTypePage;

/* ===================== REUSABLE COMPONENTS ===================== */

const FilterBox = ({
  title,
  items,
  selected,
  onSelectAll,
  onToggle,
  valueKey,
  labelKey,
  countMap,
  cols,
}) => (
  <div className="border rounded-lg p-3">
    <label className="flex justify-between font-semibold mb-2">
      {title}
      <input
        type="checkbox"
        checked={selected.length === items.length && items.length > 0}
        onChange={onSelectAll}
      />
    </label>

    <div
      className={`grid grid-cols-1 sm:grid-cols-${cols} gap-2 max-h-52 overflow-y-auto text-sm`}
    >
      {items.map((i) => (
        <label key={i[valueKey]} className="flex gap-2">
          <input
            type="checkbox"
            checked={selected.includes(i[valueKey])}
            onChange={() => onToggle(i[valueKey])}
          />
          {i[labelKey]} ({countMap[i[valueKey]] || 0})
        </label>
      ))}
    </div>
  </div>
);

const SimpleBox = ({ title, items, selected, onSelectAll, onToggle }) => (
  <div className="border rounded-lg p-3">
    <label className="flex justify-between font-semibold mb-2">
      {title}
      <input
        type="checkbox"
        checked={selected.length === items.length && items.length > 0}
        onChange={onSelectAll}
      />
    </label>
    <div className="grid grid-cols-2 gap-2 text-sm">
      {items.map((i) => (
        <label key={i.id} className="flex gap-2">
          <input
            type="checkbox"
            checked={selected.includes(i.id)}
            onChange={() => onToggle(i.id)}
          />
          {i.name}
        </label>
      ))}
    </div>
  </div>
);
